import { defineStore } from 'pinia';
import {usePage} from "@inertiajs/inertia-vue3";
import echoInstance from "../services/echo";
import {getStoreByName} from "../utils/storeHelpers";
import {getInitials} from "../utils/helpers";
import {findConflictingFocus, focusPathsOf} from "../utils/editLock.mjs";
import {t} from "@/i18n";

export const usePresenceStore = defineStore('PresenceStore', {
    state: () => {
        return {
            channel_name: null,
            present_users: {},
            fields_focused_by: {},
            /**
             * which field is currently focused by *the current* user
             */
            current_user_focus: null,
        }
    },
    getters: {
        /**
         * current (auth) user
         *
         * @see Middleware/HandleInertiaRequest
         * @returns {*}
         */
        current_user() {
            return usePage().props.value.auth.user;
        },
        getFocusFor: (state) => (path) => state.fields_focused_by[path],
        /**
         * Wer arbeitet gerade in einem Bereich, der sich mit diesem Pfad
         * überschneidet?
         *
         * Der Zuschnitt ist nicht der Pfad selbst, sondern die Liste, in der
         * er steckt: Der Server kann einen einzelnen Listeneintrag nicht
         * getrennt verrechnen, also muss die Sperre die ganze Liste umfassen.
         *
         * @see resources/js/utils/editLock.mjs
         * @returns {function(string): ({user_id: *, path: string, user: *}|null)}
         */
        conflictingFocusFor() {
            return (path) => {
                const treffer = findConflictingFocus(
                    path,
                    this.fields_focused_by,
                    this.current_user?.id
                );

                if (! treffer) {
                    return null;
                }

                return {...treffer, user: this.present_users[treffer.user_id] ?? null};
            };
        },
        getFocusUser: (state) => (path) => state.present_users[state.fields_focused_by[path]],
        /**
         * check if the given path is focused by anyone
         *
         * @param state
         * @returns {function(*): boolean}
         */
        isFocused: (state) => (path) => {
            return Boolean(state.fields_focused_by[path]).valueOf();
        },
        /**
         * get the edit hint for the focused field
         * -> use in conjunction with this.isFocused!
         *
         * @param state
         * @returns {function(*): string}
         */
        focusedBy: (state) => (path) => {
            const userId = state.fields_focused_by[path];
            const user = state.present_users[userId];

            if (!user || !user.fullName) {
                return '';
            }

            return t('users.presence.typing', {initials: getInitials(user.fullName)});
        }
    },
    actions: {
        hasValidChannelName() {
            return !(
                this.channel_name === null ||
                this.channel_name === undefined ||
                this.channel_name === '' ||
                this.channel_name === 'null' ||
                this.channel_name === 'undefined'
            );
        },
        getChannel(context = 'unknown') {
            if (!this.hasValidChannelName()) {
                console.trace(`[PresenceStore] Invalid channel_name in ${context}. Join skipped:`, this.channel_name); // i18n-ignore: Konsolenausgabe

                return null;
            }

            return echoInstance.join(this.channel_name);
        },
        bindChannelEvents() {
            const channel = this.getChannel('bindChannelEvents');

            if (!channel) {
                return;
            }

            // USE this syntax!
            // A getter which returns "echoInstance.join(this.channel_name)" does not work
            channel
                .listenForWhisper('field_focused', this.whisperReceivedFieldFocused)
                .listenForWhisper('field_changed', this.whisperReceivedFieldChanged)
                .listenForWhisper('field_blurred', this.whisperReceivedFieldBlurred);
        },
        unbindChannelEvents() {
            const channel = this.getChannel('unbindChannelEvents');

            if (!channel) {
                return;
            }

            // USE this syntax!
            // A getter which returns "echoInstance.join(this.channel_name)" does not work
            channel
                .stopListeningForWhisper('field_focused', this.whisperReceivedFieldFocused)
                .stopListeningForWhisper('field_changed', this.whisperReceivedFieldChanged)
                .stopListeningForWhisper('field_blurred', this.whisperReceivedFieldBlurred);
        },

        /**
         * the *current* user focused a field!
         * - Save the current own focus
         * - Inform everyone else on the same channel about the focus event
         *
         * @param path
         */
        fieldFocused(path) {
            this.current_user_focus = path;

            const channel = this.getChannel('fieldFocused');

            if (!channel) {
                return;
            }

            // USE this syntax!
            // A getter which returns "echoInstance.join(this.channel_name)" does not work
            channel.whisper('field_focused', {
                path,
                user_id: this.current_user.id
            });
        },
        /**
         * someone else focused a field on the current channel
         * - save the focus in this store
         *
         * @param payload
         */
        whisperReceivedFieldFocused(payload) {
            this.fields_focused_by[payload.path] = payload.user_id;
        },

        /**
         * a field has changed but not synced with the server
         * - This is primarily used with UI toggles
         *
         * @param payload{json_path, value, store_name}
         */
        fieldChanged(payload) {
            const channel = this.getChannel('fieldChanged');

            if (!channel) {
                return;
            }

            channel.whisper('field_changed', payload);
        },

        /**
         *
         *
         * @param payload{json_path, value, store_name}
         */
        whisperReceivedFieldChanged(payload) {
            const store = getStoreByName(payload.store_name);

            if (!store || typeof store.patchInStore !== 'function') {
                console.warn('[PresenceStore] Could not patch store for payload:', payload); // i18n-ignore: Konsolenausgabe

                return;
            }

            store.patchInStore({
                json_path: payload.json_path,
                value: payload.value,
            });
        },

        /**
         * the *current* user blurred a field
         * - set current own focus to null
         * - inform everyone else on the current channel about the blur event
         *
         * @param path
         */
        fieldBlurred(path) {
            this.current_user_focus = null;

            const channel = this.getChannel('fieldBlurred');

            if (!channel) {
                return;
            }

            // USE this syntax!
            // A getter which returns "echoInstance.join(this.channel_name)" does not work
            channel.whisper('field_blurred', path);
        },
        /**
         * someone else blurred a field
         * - remove saved focus from thie store
         *
         * @param path
         */
        whisperReceivedFieldBlurred(path) {
            delete this.fields_focused_by[path];
        },

        /**
         * a new user has joined the current presence channel
         * - Inform everyone about the current own focus state
         *
         * @see: resources/js/globals/utils/presenceChannel.js
         */
        syncFocused() {
            console.log("syncing focus!");

            if (!this.current_user_focus) {
                return;
            }

            const channel = this.getChannel('syncFocused');

            if (!channel) {
                return;
            }


            // USE this syntax!
            // A getter which returns "echoInstance.join(this.channel_name)" does not work
            channel
                .whisper('field_focused', {
                    path: this.current_user_focus,
                    user_id: this.current_user.id
                });

            /*
            // Debug: Fake current focus
            if (this.current_user.id === "43dc0d3d-9e29-4422-bb08-b8c7868caa8f") {
                channel
                    .whisper('field_focused', {
                        path: "$.all['43dc0d3d-4a7b-4a5a-bf7d-a66b4a6db5b4'].name",
                        user_id: this.current_user.id
                    });
            }
            */
        },
        /**
         * a user left the current channel
         * - Check if we still have a focus reference for the user
         * - remove the stale focus from the store
         *
         * @param user_id
         */
        clearStaleFocus(user_id) {
            /*
             * Alle Markierungen dieses Benutzers räumen, nicht nur die erste.
             *
             * Ein Benutzer kann mehrere halten: Der Step-Editor markiert die
             * UUID des Türchens, und jedes Textfeld darin markiert zusätzlich
             * seinen eigenen json_path. Wer nach der ersten Markierung
             * abbrach, liess die übrigen stehen - das Türchen galt dann
             * dauerhaft als "wird bearbeitet", obwohl niemand mehr da war,
             * und liess sich von niemandem mehr öffnen.
             */
            focusPathsOf(this.fields_focused_by, user_id)
                .forEach(path => this.whisperReceivedFieldBlurred(path));
        }
    }
});
